/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import currency from "currency.js";
import { MarginTypeResponse } from "../../codegen-api";
import { TEXT_COLORS } from "../../constants/design/colors";
import { formatCurrencyWithSubscript } from "../../utils/format";
import { normalizeMarginType } from "../../utils/margin";
import { getProfitTextColor } from "../../utils/strings";
import { sumKnown } from "../../utils/unknownNumbers";
import { Align } from "../shared/Table/style";
import { getColumnDataKey } from "./helpers";
import { SummaryTableRow } from "./style";
import { BuildSummaryRowArgs } from "./types";

export function buildSummaryRow({
  columns,
  modifiedPositions,
  t,
}: BuildSummaryRowArgs) {
  const totals = modifiedPositions.reduce(
    (prev, pos) => ({
      size: prev.size + Number(pos.amount),
      realizedPnl:
        prev.realizedPnl +
        Number(pos.realized_pnl || 0),
      hasRealized:
        prev.hasRealized || pos.realized_pnl !== undefined,
      delta: prev.delta + Number(pos.option?.delta) || 0,
      theta: prev.theta + Number(pos.option?.theta) || 0,
      gamma: prev.gamma + Number(pos.option?.gamma) || 0,
      vega: prev.vega + Number(pos.option?.vega) || 0,
    }),
    {
      size: 0,
      realizedPnl: 0,
      hasRealized: false,
      delta: 0,
      theta: 0,
      gamma: 0,
      vega: 0,
    }
  );

  // Unknown if ANY row's PnL is unknown (market unresolved): a total that
  // quietly counts that row as 0 is a wrong number that looks right.
  const pnlTotal = sumKnown(modifiedPositions.map((pos) => pos.unrealized_pnl));
  // Same for margin: cross maintenance margin is null for an unpriced market.
  const marginTotal = sumKnown(
    modifiedPositions.map((pos) =>
      normalizeMarginType(pos.margin_type) === MarginTypeResponse.Isolated
        ? pos.isolated_margin
        : pos.maintenance_margin
    )
  );

  return (
    // `$pnl` undefined keeps the row neutral rather than tinting it up/down.
    <SummaryTableRow $pnl={pnlTotal}>
      {columns.map((col) => {
        const columnKey = getColumnDataKey(col);
        if (col.accessor === "instrument_name") {
          return (
            <td key="instrument_name" data-column={columnKey}>
              <Align align="left">{t("summary")}</Align>
            </td>
          );
        }
        if (col.accessor === "unrealized_pnl") {
          return (
            <td key="unrealized_pnl" data-column={columnKey}>
              <Align
                align={col.align}
                style={{
                  color:
                    pnlTotal === undefined
                      ? TEXT_COLORS.three
                      : getProfitTextColor(pnlTotal),
                }}
              >
                {formatCurrencyWithSubscript(pnlTotal, {
                  showPositiveSign: true,
                })}
              </Align>
            </td>
          );
        }
        if (col.accessor === "realized_pnl") {
          return (
            <td key="realized_pnl" data-column={columnKey}>
              <Align
                align={col.align}
                style={{
                  color: totals.hasRealized
                    ? getProfitTextColor(Number(totals.realizedPnl))
                    : TEXT_COLORS.three,
                }}
              >
                {totals.hasRealized
                  ? formatCurrencyWithSubscript(totals.realizedPnl, {
                    showPositiveSign: true,
                  })
                  : "-"}
              </Align>
            </td>
          );
        }
        if (col.accessor === "maintenance_margin") {
          return (
            <td key="maintenance_margin" data-column={columnKey}>
              <Align align={col.align} style={{ color: TEXT_COLORS.one }}>
                {formatCurrencyWithSubscript(marginTotal)}
              </Align>
            </td>
          );
        }
        // @ts-ignore
        if (col.accessor === "option.delta") {
          return (
            <td key="option.delta" data-column={columnKey}>
              {/* @ts-ignore */}
              <Align align={col.align} style={{ color: TEXT_COLORS.one }}>
                {totals.delta.toFixed(2)}
              </Align>
            </td>
          );
        }
        // @ts-ignore
        if (col.accessor === "option.gamma") {
          return (
            <td key="option.gamma" data-column={columnKey}>
              {/* @ts-ignore */}
              <Align align={col.align} style={{ color: TEXT_COLORS.one }}>
                {totals.gamma.toFixed(2)}
              </Align>
            </td>
          );
        }
        // @ts-ignore
        if (col.accessor === "option.vega") {
          return (
            <td key="option.vega" data-column={columnKey}>
              {/* @ts-ignore */}
              <Align align={col.align} style={{ color: TEXT_COLORS.one }}>
                {totals.vega.toFixed(2)}
              </Align>
            </td>
          );
        }
        // @ts-ignore
        if (col.accessor === "option.theta") {
          return (
            <td key="option.theta" data-column={columnKey}>
              {/* @ts-ignore */}
              <Align align={col.align} style={{ color: TEXT_COLORS.one }}>
                {currency(totals.theta).format()}
              </Align>
            </td>
          );
        }
        return <td key={col.title || col.id} data-column={columnKey} />;
      })}
    </SummaryTableRow>
  );
}
